import { Skeleton } from '@/core/components/ui/skeleton';
import { EnergyColumnVisibility } from '../EnergyColumnDefinitions';

interface TableSkeletonProps {
  columnVisibility: EnergyColumnVisibility;
  rows?: number;
}

export function TableSkeleton({ columnVisibility, rows = 8 }: TableSkeletonProps) {
  // Calculate column widths similar to UsageTable
  const getColumnWidths = () => {
    const baseWidths = {
      tree: 25,
      sessions: 12,
      chargingStatus: 12,
      offered: 10,
      actual: 18,
      allocated: 23
    };
    
    let totalVisible = 0;
    const adjustedWidths: Record<string, number> = {};
    
    Object.entries(baseWidths).forEach(([key, width]) => {
      if (columnVisibility[key as keyof EnergyColumnVisibility]) {
        adjustedWidths[key] = width;
        totalVisible += width;
      } else {
        adjustedWidths[key] = 0;
      }
    });
    
    // Normalize to 100%
    if (totalVisible > 0) {
      Object.keys(adjustedWidths).forEach(key => {
        if (adjustedWidths[key] > 0) {
          adjustedWidths[key] = (adjustedWidths[key] / totalVisible) * 100;
        }
      });
    }
    
    return adjustedWidths;
  };

  const columnWidths = getColumnWidths();

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="scrollbar-visible">
        <div className="relative">
          {/* Header skeleton */}
          <div className="flex bg-gray-100 border-b text-sm font-medium text-gray-700 h-[45px] items-center px-4">
            {columnVisibility.tree && columnWidths.tree > 0 && (
              <div className="flex items-center justify-start" style={{ width: `${columnWidths.tree}%`, paddingLeft: '10px' }}>
                <Skeleton className="h-4 w-16" />
              </div>
            )}
            {columnVisibility.sessions && columnWidths.sessions > 0 && (
              <div className="flex items-center justify-center" style={{ width: `${columnWidths.sessions}%` }}>
                <Skeleton className="h-4 w-20" />
              </div>
            )}
            {columnVisibility.chargingStatus && columnWidths.chargingStatus > 0 && (
              <div className="flex items-center justify-center" style={{ width: `${columnWidths.chargingStatus}%` }}>
                <Skeleton className="h-4 w-24" />
              </div>
            )}
            {columnVisibility.offered && columnWidths.offered > 0 && (
              <div className="flex items-center justify-center" style={{ width: `${columnWidths.offered}%` }}>
                <Skeleton className="h-4 w-20" />
              </div>
            )}
            {columnVisibility.actual && columnWidths.actual > 0 && (
              <div className="flex items-center justify-center" style={{ width: `${columnWidths.actual}%` }}>
                <Skeleton className="h-4 w-20" />
              </div>
            )}
            {columnVisibility.allocated && columnWidths.allocated > 0 && (
              <div className="flex items-center justify-center" style={{ width: `${columnWidths.allocated}%` }}>
                <Skeleton className="h-4 w-32" />
              </div>
            )}
          </div>
          
          {/* Rows skeleton */}
          <div className="divide-y">
            {Array.from({ length: rows }).map((_, index) => (
              <div key={index} className="flex items-center h-[50px] px-4 hover:bg-gray-50">
                {columnVisibility.tree && columnWidths.tree > 0 && (
                  <div className="flex items-center" style={{ width: `${columnWidths.tree}%`, paddingLeft: '10px' }}>
                    <div className="flex items-center gap-2">
                      {/* Indentation skeleton for tree structure */}
                      <div style={{ width: `${Math.random() * 20}px` }} />
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className={`h-4 ${index % 3 === 0 ? 'w-32' : index % 3 === 1 ? 'w-24' : 'w-28'}`} />
                    </div>
                  </div>
                )}
                {columnVisibility.sessions && columnWidths.sessions > 0 && (
                  <div className="flex items-center justify-center" style={{ width: `${columnWidths.sessions}%` }}>
                    <Skeleton className="h-4 w-12" />
                  </div>
                )}
                {columnVisibility.chargingStatus && columnWidths.chargingStatus > 0 && (
                  <div className="flex items-center justify-center" style={{ width: `${columnWidths.chargingStatus}%` }}>
                    <Skeleton className="h-4 w-16" />
                  </div>
                )}
                {columnVisibility.offered && columnWidths.offered > 0 && (
                  <div className="flex items-center justify-center" style={{ width: `${columnWidths.offered}%` }}>
                    <Skeleton className="h-4 w-16" />
                  </div>
                )}
                {columnVisibility.actual && columnWidths.actual > 0 && (
                  <div className="flex items-center justify-center" style={{ width: `${columnWidths.actual}%` }}>
                    <Skeleton className="h-4 w-16" />
                  </div>
                )}
                {columnVisibility.allocated && columnWidths.allocated > 0 && (
                  <div className="flex items-center justify-center" style={{ width: `${columnWidths.allocated}%` }}>
                    <Skeleton className="h-4 w-20" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
